import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faPencil, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { GetCountry } from "api/rpc/2022-09/common/country/country";
import {
  GetCustomer,
  PostCustomerAddressLine,
  PutCustomerAddressLine,
  DeleteCustomerAddressLine,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { IAddress, ICountry, ICustomer } from "redux/reducers/models/customer";

import Sheet from "components/sheet/Sheet";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import Toggle from "components/form/toggle/Toggle";
import { ButtonNew as Button } from "components/buttonNew";

import "./invoice.scss";

const BILLING_ACTION = "__billing_action__";
const SHIPPING_ACTION = "__shipping_action__";
const NONE = "none";

type AddressSheetKind = "billing" | "shipping";
type AddressSheetMode = "add" | "manage";
type AddressSheetState = { kind: AddressSheetKind; mode: AddressSheetMode };

type AddressForm = {
  id?: number;
  address_line_1: string;
  address_line_2: string;
  city: string;
  postal: string;
  country_id: number | null;
  province_id: number | null;
};

type Props = {
  open: boolean;
  customer?: ICustomer;
  billingAddressId?: number;
  shippingAddressId?: number;
  onCancel: () => void;
  onUpdate: (next: {
    customer: ICustomer;
    billingAddress: IAddress | null;
    shippingAddress: IAddress | null;
  }) => void;
};

function emptyAddress(countries?: ICountry[]): AddressForm {
  return {
    address_line_1: "",
    address_line_2: "",
    city: "",
    postal: "",
    country_id: countries?.[0]?.id ?? null,
    province_id: countries?.[0]?.provinces?.[0]?.id ?? null,
  };
}

function addressToForm(address?: IAddress, countries?: ICountry[]): AddressForm {
  if (!address) {
    return emptyAddress(countries);
  }
  return {
    id: address.id,
    address_line_1: address.address_line_1 || "",
    address_line_2: address.address_line_2 || "",
    city: address.city || "",
    postal: address.postal || "",
    country_id: address.country_id,
    province_id: address.province_id,
  };
}

function formatAddressLabel(address: IAddress) {
  return [address.address_line_1, address.city, address.province_name, address.postal].filter(Boolean).join(", ");
}

export default function InvoiceCustomerDetailsModal(props: Props) {
  const { open, onCancel, onUpdate } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;

  const [addressSheet, setAddressSheet] = useState<AddressSheetState | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer>(null);
  const [billingAddressId, setBillingAddressId] = useState<number | typeof NONE>(NONE);
  const [shippingAddressId, setShippingAddressId] = useState<number | typeof NONE>(NONE);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerResults, setCustomerResults] = useState<ICustomer[]>([]);
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [addressForms, setAddressForms] = useState<AddressForm[]>([emptyAddress()]);
  const [activeAddressIndex, setActiveAddressIndex] = useState(0);
  const [sameAsOtherByIndex, setSameAsOtherByIndex] = useState<Record<number, boolean>>({});
  const [savingAddress, setSavingAddress] = useState(false);
  const [selectReset, setSelectReset] = useState(0);

  const addressLines = selectedCustomer?.address_lines ?? [];
  const hasAddresses = addressLines.length > 0;

  useEffect(() => {
    if (!open) {
      setAddressSheet(null);
      return;
    }

    setSelectedCustomer(props.customer ?? null);
    setBillingAddressId(props.billingAddressId ?? NONE);
    setShippingAddressId(props.shippingAddressId ?? NONE);
    setCustomerQuery(props.customer?.full_name || "");
    setCustomerResults(props.customer ? [props.customer] : []);
    setSameAsOtherByIndex({});
    setSelectReset(0);
    void loadCountries();

    if (props.customer?.id) {
      void refreshCustomer(props.customer.id);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !addressSheet) {
      return;
    }
    if (countries.length === 0) {
      return;
    }

    setAddressForms(prev =>
      prev.map(form => ({
        ...form,
        country_id: form.country_id ?? countries[0].id,
        province_id: form.province_id ?? countries[0].provinces?.[0]?.id ?? null,
      })),
    );
  }, [countries, open, addressSheet]);

  useEffect(() => {
    if (!open || addressSheet) {
      return;
    }

    const timeoutId = global.setTimeout(() => {
      void searchCustomers(customerQuery);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [customerQuery, open, addressSheet]);

  async function loadCountries() {
    if (countries.length > 0) {
      return;
    }
    const countryRes = await GetCountry({ provinces: true }, false);
    if (countryRes.status === StatusCode.OK) {
      setCountries(countryRes.data);
    }
  }

  async function searchCustomers(query: string) {
    if (!query) {
      setCustomerResults(selectedCustomer ? [selectedCustomer] : []);
      setCustomerSearching(false);
      return;
    }

    setCustomerSearching(true);
    const customerRes = await GetCustomer({ search: query, "address-lines": true }, false);
    if (customerRes.status !== StatusCode.OK) {
      setCustomerSearching(false);
      setCustomerResults([]);
      return;
    }
    setCustomerSearching(false);
    setCustomerResults(customerRes.data ?? []);
  }

  async function refreshCustomer(customerId: number) {
    const customerRes = await GetCustomer({ id: customerId, "address-lines": true }, false);
    if (customerRes.status === StatusCode.OK && customerRes.data?.[0]) {
      const customer = customerRes.data[0];
      setSelectedCustomer(customer);
      setCustomerResults(prev => {
        const withoutCurrent = prev.filter(item => item.id !== customer.id);
        return [customer, ...withoutCurrent];
      });
      return customer;
    }
    return selectedCustomer;
  }

  function handleSelectCustomer(customer: ICustomer) {
    if (!customer?.id) {
      return;
    }

    setSelectedCustomer(customer);
    setCustomerQuery(customer.full_name || "");
    setBillingAddressId(NONE);
    setShippingAddressId(NONE);

    if (!customer.address_lines) {
      void refreshCustomer(customer.id);
    }
  }

  function openAddressSheet(kind: AddressSheetKind) {
    setSelectReset(value => value + 1);
    const mode: AddressSheetMode = addressLines.length > 0 ? "manage" : "add";

    if (mode === "manage") {
      const selectedId = kind === "billing" ? billingAddressId : shippingAddressId;
      const forms = addressLines.map(address => addressToForm(address, countries));
      const activeIndex = Math.max(
        0,
        forms.findIndex(form => form.id === selectedId),
      );
      setAddressForms(forms);
      setActiveAddressIndex(activeIndex === -1 ? 0 : activeIndex);
    } else {
      setAddressForms([emptyAddress(countries)]);
      setActiveAddressIndex(0);
    }

    setSameAsOtherByIndex({});
    setAddressSheet({ kind, mode });
  }

  function updateAddressForm(index: number, patch: Partial<AddressForm>) {
    setActiveAddressIndex(index);
    setAddressForms(prev => prev.map((form, formIndex) => (formIndex === index ? { ...form, ...patch } : form)));
  }

  function handleSameAsOther(index: number, checked: boolean) {
    setActiveAddressIndex(index);
    setSameAsOtherByIndex(prev => ({ ...prev, [index]: checked }));
    if (!checked || !addressSheet) {
      return;
    }

    const sourceId = addressSheet.kind === "billing" ? shippingAddressId : billingAddressId;
    const source = addressLines.find(address => address.id === sourceId);
    if (!source) {
      return;
    }

    const copied = addressToForm(source, countries);
    setAddressForms(prev =>
      prev.map((form, formIndex) =>
        formIndex === index
          ? {
              ...copied,
              id: form.id,
            }
          : form,
      ),
    );
  }

  async function saveAddress() {
    if (!selectedCustomer?.id || !addressSheet) {
      return;
    }

    const incomplete = addressForms.some(
      form => !form.address_line_1 || !form.country_id || !form.province_id,
    );
    if (incomplete) {
      dispatch(showError(t("Please complete the address")));
      return;
    }

    setSavingAddress(true);
    let preferredId: number | typeof NONE = addressForms[activeAddressIndex]?.id ?? NONE;

    for (let index = 0; index < addressForms.length; index++) {
      const form = addressForms[index];
      const payload = {
        customer_id: selectedCustomer.id,
        address_line_1: form.address_line_1,
        address_line_2: form.address_line_2,
        city: form.city,
        postal: form.postal,
        country_id: form.country_id,
        province_id: form.province_id,
        default: false,
      };

      const res =
        form.id != null
          ? await PutCustomerAddressLine({ ...payload, id: form.id }, true)
          : await PostCustomerAddressLine(payload, true);

      if (res.status !== StatusCode.OK) {
        setSavingAddress(false);
        dispatch(showError(t("Error saving address")));
        return;
      }

      if (index === activeAddressIndex) {
        preferredId = form.id ?? res.data?.id ?? preferredId;
      }
    }

    setSavingAddress(false);
    dispatch(showSuccess(t("Address saved")));
    const updatedCustomer = await refreshCustomer(selectedCustomer.id);

    const nextId =
      preferredId !== NONE
        ? preferredId
        : updatedCustomer?.address_lines?.[updatedCustomer.address_lines.length - 1]?.id ?? NONE;

    if (addressSheet.kind === "billing") {
      setBillingAddressId(nextId);
    } else {
      setShippingAddressId(nextId);
    }

    setAddressSheet(null);
  }

  async function removeAddress(index: number) {
    if (!addressSheet) {
      return;
    }

    const target = addressForms[index];
    if (!target) {
      return;
    }

    setActiveAddressIndex(index);

    if (!selectedCustomer?.id || target.id == null) {
      if (addressForms.length <= 1) {
        setAddressForms([emptyAddress(countries)]);
        setActiveAddressIndex(0);
        setSameAsOtherByIndex({});
        setAddressSheet(prev => (prev ? { ...prev, mode: "add" } : prev));
        return;
      }

      setAddressForms(prev => prev.filter((_, formIndex) => formIndex !== index));
      setSameAsOtherByIndex({});
      setActiveAddressIndex(prev => Math.max(0, Math.min(prev === index ? index : prev > index ? prev - 1 : prev, addressForms.length - 2)));
      return;
    }

    const res = await DeleteCustomerAddressLine(
      { customer_id: selectedCustomer.id, address_line_id: target.id },
      true,
    );
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing address")));
      return;
    }

    dispatch(showSuccess(t("Address removed")));
    const updatedCustomer = await refreshCustomer(selectedCustomer.id);
    const remaining = (updatedCustomer?.address_lines ?? []).map(address => addressToForm(address, countries));

    if (addressSheet.kind === "billing" && billingAddressId === target.id) {
      setBillingAddressId(NONE);
    }
    if (addressSheet.kind === "shipping" && shippingAddressId === target.id) {
      setShippingAddressId(NONE);
    }

    setSameAsOtherByIndex({});

    if (remaining.length === 0) {
      setAddressForms([emptyAddress(countries)]);
      setActiveAddressIndex(0);
      setAddressSheet(prev => (prev ? { ...prev, mode: "add" } : prev));
      return;
    }

    setAddressForms(remaining);
    setActiveAddressIndex(0);
    setAddressSheet(prev => (prev ? { ...prev, mode: "manage" } : prev));
  }

  function addNewAddress() {
    setAddressForms(prev => [...prev, emptyAddress(countries)]);
    setActiveAddressIndex(addressForms.length);
    setSameAsOtherByIndex(prev => ({ ...prev, [addressForms.length]: false }));
    setAddressSheet(prev => (prev ? { ...prev, mode: prev.mode === "manage" ? "manage" : "add" } : prev));
  }

  function handleUpdateChanges() {
    const billing =
      billingAddressId === NONE ? null : addressLines.find(address => address.id === billingAddressId) ?? null;
    const shipping =
      shippingAddressId === NONE ? null : addressLines.find(address => address.id === shippingAddressId) ?? null;

    onUpdate({
      customer: selectedCustomer,
      billingAddress: billing,
      shippingAddress: shipping,
    });
  }

  function renderAddressForm(form: AddressForm, index: number, kind: AddressSheetKind) {
    const selectedCountry = countries.find(country => country.id === form.country_id);
    const isActive = index === activeAddressIndex;
    const showRemove = addressSheet?.mode === "manage" || addressForms.length > 1 || !!form.id;

    return (
      <div
        key={form.id ?? `new-${index}`}
        className={
          isActive
            ? "invoice-customer-details-modal__address invoice-customer-details-modal__address--active"
            : "invoice-customer-details-modal__address"
        }
        onFocus={() => setActiveAddressIndex(index)}
      >
        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Address line 1")}
                value={form.address_line_1}
                onChange={e => updateAddressForm(index, { address_line_1: e.target.value })}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Address line 2")}
                value={form.address_line_2}
                onChange={e => updateAddressForm(index, { address_line_2: e.target.value })}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("City")}
                value={form.city}
                onChange={e => updateAddressForm(index, { city: e.target.value })}
              />
              <Input
                label={t("Postal code")}
                value={form.postal}
                onChange={e => updateAddressForm(index, { postal: e.target.value })}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                key={`country-${index}-${form.country_id ?? "none"}`}
                label={t("Country")}
                onChange={(value: number) => {
                  const country = countries.find(item => item.id === value);
                  updateAddressForm(index, {
                    country_id: value,
                    province_id: country?.provinces?.[0]?.id ?? null,
                  });
                }}
                defaultValue={form.country_id}
              >
                {countries.map(country => (
                  <Option key={country.id} value={country.id} name={country.name}>
                    {country.name}
                  </Option>
                ))}
              </Select>
              <Select
                key={`province-${index}-${form.province_id ?? "none"}`}
                label={t("Province")}
                onChange={(value: number) => updateAddressForm(index, { province_id: value })}
                defaultValue={form.province_id}
              >
                {selectedCountry?.provinces?.map(province => (
                  <Option key={province.id} value={province.id} name={province.name}>
                    {province.name}
                  </Option>
                ))}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Form>

        <div className="invoice-customer-details-modal__row">
          <Toggle
            id={`${kind}-same-as-other-${index}`}
            labelRight={
              kind === "billing"
                ? t("Billing address same as shipping address")
                : t("Shipping address same as billing address")
            }
            checked={!!sameAsOtherByIndex[index]}
            onChange={e => handleSameAsOther(index, e.target.checked)}
            size="small"
            rounded
          />
          {showRemove && (
            <Button type="text" size="small" onClick={() => void removeAddress(index)}>
              <span className="invoice-text-error invoice-customer-details-modal__action-option">
                <FontAwesomeIcon icon={faTrashCan} />
                {t("Remove address")}
              </span>
            </Button>
          )}
        </div>
      </div>
    );
  }

  function renderAddNewAction() {
    return (
      <div className="invoice-customer-details-modal__actions">
        <Button type="text" size="small" onClick={addNewAddress}>
          <span className="invoice-customer-details-modal__action-option">
            <FontAwesomeIcon icon={faPlus} />
            {t("Add new")}
          </span>
        </Button>
      </div>
    );
  }

  function renderAddressSheet(kind: AddressSheetKind) {
    const isOpen = open && addressSheet?.kind === kind;
    const isManage = addressSheet?.mode === "manage";
    const customerName = selectedCustomer?.full_name || "";

    return (
      <Sheet
        open={isOpen}
        size="small"
        stacked
        closable
        title={
          isManage
            ? kind === "billing"
              ? t("Manage billing address")
              : t("Manage shipping address")
            : kind === "billing"
              ? t("Add billing address")
              : t("Add shipping address")
        }
        supportText={
          isManage
            ? kind === "billing"
              ? t("Edit or add billing address for {{name}}.", { name: customerName })
              : t("Edit or add shipping address for {{name}}.", { name: customerName })
            : kind === "billing"
              ? t("Add billing address for {{name}}.", { name: customerName })
              : t("Add shipping address for {{name}}.", { name: customerName })
        }
        cancelText={t("Go back")}
        okText={t("Save changes")}
        okLoading={savingAddress}
        onCancel={() => setAddressSheet(null)}
        onOk={() => void saveAddress()}
      >
        <div className="invoice-customer-details-modal">
          <div className="invoice-customer-details-modal__scroll">
            {addressForms.map((form, index) => renderAddressForm(form, index, kind))}
            {renderAddNewAction()}
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet
        open={open}
        size="small"
        closable
        overflow
        title={t("Edit customer details")}
        supportText={t(
          "Search and change the customer this invoice is addressed to. Addresses will auto-fill once selected.",
        )}
        cancelText={t("Cancel")}
        okText={t("Update changes")}
        onCancel={onCancel}
        onOk={handleUpdateChanges}
        okDisabled={!selectedCustomer}
      >
        <div className="invoice-customer-details-modal">
          <Select
            label={t("Customer")}
            showSearch
            searching={customerSearching}
            searchValue={customerQuery}
            onSearch={setCustomerQuery}
            onChange={(_value: number, customer: ICustomer) => handleSelectCustomer(customer)}
            defaultValue={selectedCustomer?.id}
            showDropDownOnFocus
            placeholder={t("Search by name, email, phone or member number...")}
          >
            {customerResults.map(customer => (
              <Option key={customer.id} value={customer.id} extraValues={customer} name={customer.full_name}>
                {customer.full_name}
              </Option>
            ))}
          </Select>

          <Select
            key={`billing-${selectedCustomer?.id ?? "none"}-${billingAddressId}-${selectReset}`}
            label={t("Billing address")}
            onChange={(value: number | string) => {
              if (value === BILLING_ACTION) {
                openAddressSheet("billing");
                return;
              }
              setBillingAddressId(value === NONE ? NONE : Number(value));
            }}
            defaultValue={billingAddressId}
            showDropDownOnFocus
            disabled={!selectedCustomer}
          >
            <Option value={NONE} name={t("None")}>
              {t("None")}
            </Option>
            {addressLines.map(address => (
              <Option key={address.id} value={address.id} name={formatAddressLabel(address)}>
                {formatAddressLabel(address)}
              </Option>
            ))}
            <Option
              value={BILLING_ACTION}
              name={hasAddresses ? t("Manage billing address") : t("Add billing address")}
            >
              <span className="invoice-customer-details-modal__action-option">
                <FontAwesomeIcon icon={hasAddresses ? faPencil : faPlus} />
                {hasAddresses ? t("Manage billing address") : t("Add billing address")}
              </span>
            </Option>
          </Select>

          <Select
            key={`shipping-${selectedCustomer?.id ?? "none"}-${shippingAddressId}-${selectReset}`}
            label={t("Shipping address")}
            onChange={(value: number | string) => {
              if (value === SHIPPING_ACTION) {
                openAddressSheet("shipping");
                return;
              }
              setShippingAddressId(value === NONE ? NONE : Number(value));
            }}
            defaultValue={shippingAddressId}
            showDropDownOnFocus
            disabled={!selectedCustomer}
          >
            <Option value={NONE} name={t("None")}>
              {t("None")}
            </Option>
            {addressLines.map(address => (
              <Option key={address.id} value={address.id} name={formatAddressLabel(address)}>
                {formatAddressLabel(address)}
              </Option>
            ))}
            <Option
              value={SHIPPING_ACTION}
              name={hasAddresses ? t("Manage shipping address") : t("Add shipping address")}
            >
              <span className="invoice-customer-details-modal__action-option">
                <FontAwesomeIcon icon={hasAddresses ? faPencil : faPlus} />
                {hasAddresses ? t("Manage shipping address") : t("Add shipping address")}
              </span>
            </Option>
          </Select>
        </div>
      </Sheet>

      {renderAddressSheet("billing")}
      {renderAddressSheet("shipping")}
    </>
  );
}
